import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Settings, Eye, PlayCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { GoogleCalendarIntegration } from './GoogleCalendarIntegration';
import PractitionerPasswordForm from './Security/PractitionerPasswordForm';
import PractitionerAddPasswordForm from './Security/PractitionerAddPasswordForm';
import { PractitionerReferralSettings } from './PractitionerReferralSettings';

interface PractitionerSettingsProps {
  practitionerId: string;
  profile: any;
  setProfile: (profile: any) => void;
}

const PractitionerSettings: React.FC<PractitionerSettingsProps> = ({ 
  practitionerId, 
  profile, 
  setProfile 
}) => {
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const [isSaving, setIsSaving] = useState(false);
  const [acceptingNewClients, setAcceptingNewClients] = useState<boolean>(profile?.accepting_new_clients ?? true);
  const [hasPasswordAuth, setHasPasswordAuth] = useState(false);

  // Check if user has password authentication
  useEffect(() => {
    const checkAuthMethods = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const hasPassword = user.app_metadata?.providers?.includes('email') || 
                           user.identities?.some(identity => identity.provider === 'email');
        setHasPasswordAuth(hasPassword);
      }
    };
    checkAuthMethods();
  }, []);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('practitioners')
        .update({
          accepting_new_clients: acceptingNewClients
        })
        .eq('id', practitionerId);

      if (error) throw error;

      // Optimistically update the profile
      setProfile(prevProfile => ({
        ...prevProfile,
        accepting_new_clients: acceptingNewClients
      }));

      toast({
        title: "Success",
        description: "Settings saved successfully",
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset onboarding tour
  const resetOnboardingTour = () => {
    localStorage.removeItem('onboarding_completed_practitioner');
    window.location.reload();
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            General Settings
          </CardTitle>
          <CardDescription>
            Manage your basic profile settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="acceptingNewClients" className="flex items-center text-sm">
              <Eye className="w-4 h-4 mr-2" />
              Accepting New Clients
            </Label>
            <div className="space-y-1">
              <Switch
                id="acceptingNewClients"
                checked={acceptingNewClients}
                onCheckedChange={(checked) => setAcceptingNewClients(checked)}
              />
              <p className="text-xs text-muted-foreground">
                Control whether your availability is visible to clients.
              </p>
            </div>
          </div>



          <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full sm:w-auto">
            {isSaving ? (
              <>
                <Save className="w-4 h-4 mr-2 animate-pulse" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Security - Add/Change Password */}
      {!hasPasswordAuth ? (
        <PractitionerAddPasswordForm onPasswordAdded={() => setHasPasswordAuth(true)} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Change Password</CardTitle>
            <CardDescription>Update your account password.</CardDescription>
          </CardHeader>
          <CardContent>
            <PractitionerPasswordForm />
          </CardContent>
        </Card>
      )}

      {/* Google Calendar Integration Card */}
      <GoogleCalendarIntegration />

      {/* Referral Settings */}
      <PractitionerReferralSettings />

      {/* Dashboard Guide Card */}
      <Card>
        <CardHeader>
          <CardTitle>Dashboard Guide</CardTitle>
          <CardDescription>Restart the dashboard walkthrough tutorial to learn about all features.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={resetOnboardingTour}
            className="w-full sm:w-auto"
          >
            <PlayCircle className="w-4 h-4 mr-2" />
            Restart Guide
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PractitionerSettings;
